import { useMemo, useState } from 'react';

const asText = (value) => String(value ?? '').trim();
const digitsOnly = (value) => asText(value).replace(/\D/g, '');

const buildSearchText = (item) => [
  item?.device_name,
  item?.product_name,
  item?.name,
  item?.device_oem_name,
  item?.device_type,
  item?.product_type,
  item?.type,
  item?.imei,
  item?.sim_no,
  item?.sim_no_2,
  item?.serial_no,
  item?.warehouse,
  item?.status
].map(asText).filter(Boolean).join(' ').toLowerCase();

const scoreDevice = (item, query) => {
  const term = asText(query).toLowerCase();
  const termDigits = digitsOnly(term);
  if (!term) return 50;

  const imeiDigits = digitsOnly(item?.imei);
  const simDigits = `${digitsOnly(item?.sim_no)} ${digitsOnly(item?.sim_no_2)} ${digitsOnly(item?.serial_no)}`;
  const searchText = buildSearchText(item);

  if (termDigits.length >= 5 && imeiDigits.endsWith(termDigits)) return 0;
  if (termDigits.length >= 5 && simDigits.includes(termDigits)) return 1;
  if (imeiDigits.includes(termDigits) && termDigits.length >= 3) return 2;
  if (searchText.includes(term)) return 3;
  return 999;
};

export const inventoryDeviceMatchesSearch = (item, query) => scoreDevice(item, query) < 999;

export const inventoryDeviceLabel = (item) => {
  const name = item?.device_name || item?.product_name || item?.name || 'Inventory device';
  const imei = item?.imei ? `IMEI ${item.imei}` : 'No IMEI';
  const sim = item?.sim_no || item?.sim_no_2 || item?.serial_no;
  const warehouse = item?.warehouse || 'Main';
  return `${name} | ${imei}${sim ? ` | SIM ${sim}` : ''} | ${warehouse}`;
};

export default function InventoryDevicePicker({
  items = [],
  value = '',
  onChange,
  label = 'Inventory Device',
  placeholder = 'Search IMEI last 5, SIM, serial, device',
  emptyMessage = 'No matching device found',
  disabled = false,
  required = false,
  showSearchModes = false,
  maxResults = 8,
  inputStyle = {},
  labelStyle = {}
}) {
  const [query, setQuery] = useState('');
  const [searchMode, setSearchMode] = useState('imei');
  const selectedItem = useMemo(
    () => items.find((item) => String(item.id) === String(value)) || null,
    [items, value]
  );
  const effectivePlaceholder = showSearchModes && searchMode === 'imei'
    ? 'Enter last 5 digits of IMEI'
    : showSearchModes
      ? 'Search SIM, serial, device name, office location'
      : placeholder;
  const trimmedQuery = query.trim();
  const hasSearchTerm = showSearchModes && searchMode === 'imei'
    ? digitsOnly(trimmedQuery).length >= 5
    : trimmedQuery.length > 0;

  const matches = useMemo(() => {
    if (!hasSearchTerm) return [];
    const rows = [...items]
      .map((item, index) => ({ item, index, score: scoreDevice(item, query) }))
      .filter((row) => row.score < 999)
      .sort((a, b) => a.score - b.score || a.index - b.index)
      .map((row) => row.item);
    return rows.slice(0, maxResults);
  }, [hasSearchTerm, items, maxResults, query]);

  const totalMatches = useMemo(
    () => (hasSearchTerm ? items.filter((item) => inventoryDeviceMatchesSearch(item, query)).length : 0),
    [hasSearchTerm, items, query]
  );

  const selectItem = (item) => {
    onChange?.(item ? String(item.id) : '', item || null);
    if (item) setQuery('');
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    if (matches.length === 0) return;
    event.preventDefault();

    const termDigits = digitsOnly(query);
    const exactLastFiveMatches = termDigits.length >= 5
      ? matches.filter((item) => digitsOnly(item.imei).endsWith(termDigits))
      : [];
    if (exactLastFiveMatches.length === 1) {
      selectItem(exactLastFiveMatches[0]);
      return;
    }
    if (matches.length === 1) selectItem(matches[0]);
  };

  const showResults = !disabled && hasSearchTerm;

  return (
    <div>
      {label && <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 'bold', color: '#374151', ...labelStyle }}>{label}{required ? ' *' : ''}</label>}
      {showSearchModes && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
          {[
            { key: 'imei', label: 'IMEI Last 5' },
            { key: 'other', label: 'SIM / Serial / Device' }
          ].map((mode) => (
            <button
              key={mode.key}
              type="button"
              onClick={() => {
                setSearchMode(mode.key);
                setQuery('');
              }}
              disabled={disabled}
              style={{
                border: `1px solid ${searchMode === mode.key ? '#0b66ae' : '#dbeafe'}`,
                background: searchMode === mode.key ? '#0b66ae' : '#ffffff',
                color: searchMode === mode.key ? '#ffffff' : '#0b66ae',
                borderRadius: '8px',
                padding: '9px 10px',
                fontSize: '12px',
                fontWeight: 900,
                cursor: disabled ? 'not-allowed' : 'pointer'
              }}
            >
              {mode.label}
            </button>
          ))}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: selectedItem ? '1fr auto' : '1fr', gap: '8px' }}>
        <input
          style={inputStyle}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={effectivePlaceholder}
          disabled={disabled}
          required={required && !selectedItem}
        />
        {selectedItem && (
          <button
            type="button"
            onClick={() => selectItem(null)}
            disabled={disabled}
            style={{
              border: '1px solid #fecaca',
              background: '#fff1f2',
              color: '#be123c',
              borderRadius: '8px',
              padding: '0 12px',
              fontSize: '12px',
              fontWeight: 800,
              cursor: disabled ? 'not-allowed' : 'pointer'
            }}
          >
            Clear
          </button>
        )}
      </div>

      {selectedItem && (
        <div style={{ marginTop: '8px', border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#166534', borderRadius: '8px', padding: '9px 10px', fontSize: '12px', fontWeight: 800 }}>
          Device selected
        </div>
      )}

      {showResults && (
        <div style={{ marginTop: '8px', display: 'grid', gap: '6px' }}>
          {matches.length === 0 ? (
            <div style={{ border: '1px dashed #cbd5e1', color: '#64748b', borderRadius: '8px', padding: '10px', fontSize: '12px' }}>
              {items.length === 0 ? emptyMessage : 'No device matched this search'}
            </div>
          ) : matches.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => selectItem(item)}
              style={{
                width: '100%',
                border: String(item.id) === String(value) ? '1px solid #10b981' : '1px solid #e2e8f0',
                background: String(item.id) === String(value) ? '#ecfdf5' : '#ffffff',
                color: '#0f172a',
                borderRadius: '8px',
                padding: '9px 10px',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 700,
                lineHeight: 1.35
              }}
            >
              {inventoryDeviceLabel(item)}
            </button>
          ))}
          {totalMatches > matches.length && (
            <small style={{ color: '#64748b', fontSize: '12px' }}>
              Showing {matches.length} of {totalMatches} matches
            </small>
          )}
        </div>
      )}
    </div>
  );
}
